/**
 * The line for a failed request that is NOT the server judging the person's
 * input (#1338, owner Q-374 a). A screen shows its input-blaming line only on
 * the one status that means "your input" and routes every other failure here:
 *
 *   - 429                              -> `alertMessages.auth.tooManyRequests`
 *   - transport (timeout / no network) -> `formMessages.connectivity.unreachable`
 *   - everything else (5xx, another 4xx, the circuit-breaker cancel)
 *                                      -> `SERVER_FAILURE_MESSAGE`
 *
 * No new wording: all three strings pre-exist.
 */
import { SERVER_FAILURE_MESSAGE, alertMessages } from '@/constants/alertMessages';
import { formMessages } from '@/constants/formMessages';
import { classifyFailure } from '@/api/failureClass';
import { verifyLockoutWords } from '@/lib/lockoutWords';
import { extractApiErrorData, extractApiMessage, isAxiosErrorWithResponse } from '@/lib/apiError';

export function nonInputFailureMessage(error: unknown): string {
  const cls = classifyFailure(error);
  if (cls === 'transport_timeout' || cls === 'transport_network') {
    return formMessages.connectivity.unreachable;
  }
  if (cls === 'client' && isAxiosErrorWithResponse(error) && error.response.status === 429) {
    return alertMessages.auth.tooManyRequests;
  }
  return SERVER_FAILURE_MESSAGE;
}

/** The HTTP status of a response-bearing axios error, else undefined. */
export function failureStatus(error: unknown): number | undefined {
  return isAxiosErrorWithResponse(error) ? error.response.status : undefined;
}

/**
 * A 400 (or the verify lockout's 423, vingapi#1946) shows the server's own sentence when it sent a string (or `{message}`)
 * `detail`; a 400 without one (no body, a LIST `detail`) and every other
 * failure get `nonInputFailureMessage`. Status alone never selects a fixed
 * input-blaming line: the verify lockout is also a 400 (#1338, PM 10-07).
 */
export function serverSentenceOrNonInput(error: unknown): string {
  const status = failureStatus(error);
  if ((status === 400 || status === 423) && !Array.isArray(extractApiErrorData(error)?.detail)) {
    return extractApiMessage(error, SERVER_FAILURE_MESSAGE);
  }
  return nonInputFailureMessage(error);
}

/**
 * `serverSentenceOrNonInput` for a call to `/api/auth/verify/` (#1566 a): a 423
 * is the verify lockout and says the approved words (Q-678), the server's own
 * when it sent the coded envelope.
 */
export function verifyFailureMessage(error: unknown): string {
  return failureStatus(error) === 423 ? verifyLockoutWords(error) : serverSentenceOrNonInput(error);
}
